import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

/**
 * Which lens the agency is looking through.
 *
 * An agency admin sees things a client editor never does - the public read API address, Sync from
 * site, Handover, Settings, the client list. That is correct, but it leaves the agency with no way
 * to know what their client actually sees. "Client view" answers that: it hides every agency-only
 * affordance so the page is exactly what the client gets.
 *
 * This is a lens over the UI, nothing more. It grants nobody anything: the backend still decides
 * permissions from the signed session on every request, so a client editor cannot reach agency
 * screens by fiddling with this, and an agency admin in client view is still an agency admin as
 * far as the API is concerned.
 */
const VIEW_MODE_KEY = "rp_view_mode";

const readStoredViewMode = () => {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === "client" ? "client" : "agency";
  } catch {
    return "agency";
  }
};

const storeViewMode = (mode) => {
  try {
    if (mode === "client") window.localStorage.setItem(VIEW_MODE_KEY, "client");
    else window.localStorage.removeItem(VIEW_MODE_KEY);
  } catch {
    /* private browsing - the lens just will not persist */
  }
};

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: "loading",
    user: null,
    tenants: [],
    agencyName: "Review Presence",
  });
  const [viewMode, setViewModeState] = useState(readStoredViewMode);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({
        status: "authenticated",
        user: data.user,
        tenants: data.tenants || [],
        agencyName: data.agency_name || "Review Presence",
      });
    } catch (error) {
      const code = error?.response?.status;
      setState({
        status: code === 403 ? "forbidden" : "anonymous",
        user: null,
        tenants: [],
        agencyName: "Review Presence",
      });
    }
  }, []);

  useEffect(() => {
    // Arriving from sign-in? The one-time id has not been spent yet, so asking /auth/me now
    // would 401 and bounce the person straight back to the sign-in screen. AuthCallback
    // establishes the session first and then calls refresh().
    if (String(window.location.hash).includes("session_id=")) {
      setState({
        status: "anonymous",
        user: null,
        tenants: [],
        agencyName: "Review Presence",
      });
      return;
    }
    load();
  }, [load]);

  const setViewMode = useCallback((mode) => {
    const next = mode === "client" ? "client" : "agency";
    storeViewMode(next);
    setViewModeState(next);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* signing out locally is enough */
    }
    // Never leave the lens on for whoever signs in next.
    storeViewMode("agency");
    setViewModeState("agency");
    setState({ status: "anonymous", user: null, tenants: [], agencyName: "Review Presence" });
  }, []);

  const value = useMemo(() => {
    const isAgencyAccount = !!state.user?.is_agency;
    return {
      ...state,
      refresh: load,
      logout,
      /** The real role on the session. Use this to decide who may switch views. */
      isAgencyAccount,
      /** Which lens is active. Only meaningful for an agency account. */
      viewMode: isAgencyAccount ? viewMode : "client",
      setViewMode,
      /**
       * What the UI should show. Everything that renders agency-only chrome reads this, so
       * switching to client view hides all of it in one move.
       */
      isAgency: isAgencyAccount && viewMode === "agency",
    };
  }, [state, load, logout, viewMode, setViewMode]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
